import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { gql, useLazyQuery } from "@apollo/client";
import { useAuth } from "../../../context/AuthContext";
import { API_URL } from "./constants";
import "./UsersTab.css";

// Same admin-only search the Rewards tab picker uses: email, name, and
// subscriber status per socialUsers row.
const SEARCH_SOCIAL_USERS = gql`
  query SearchSocialUsersForPairing($query: String!) {
    searchSocialUsers(query: $query) {
      userId
      email
      firstName
      lastName
      name
      phone
      paid
      approved
      stripeEmail
    }
  }
`;

const SEARCH_DEBOUNCE_MS = 300;

// Admin-only Users tab: the full Cognito user pool, newest joiners
// first, with each account's current access level and one-click role
// assignment. This is the "someone just signed up — approve them /
// make them a captain / mark them a subscriber" screen.
//
// Data: GET ?type=cognitoUsers (pool roster + Admins/Captains group
// membership + socialUsers approved/paid/sex join). Actions reuse the
// Team tab's promoteCaptain/demoteCaptain endpoints for captains and
// POST setUserAccess for the approved / paid flags (which creates the
// socialUsers row when a legacy account lacks one).

const FILTERS = [
  { id: "all", label: "Everyone" },
  { id: "new7", label: "Joined this week" },
  { id: "unapproved", label: "Not approved yet" },
  { id: "subscribers", label: "Subscribers" },
  { id: "captains", label: "Captains" },
];

const formatJoined = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
};

const isWithinDays = (iso, days) => {
  if (!iso) return false;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return false;
  return Date.now() - t <= days * 24 * 60 * 60 * 1000;
};

const UsersTab = () => {
  const { getAuthHeaders, userInfo } = useAuth();
  const [users, setUsers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [busyKey, setBusyKey] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  // Cognito account currently being paired with a socialUsers row via
  // the LinkProfileModal (null = modal closed).
  const [linking, setLinking] = useState(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const resp = await fetch(`${API_URL}?type=cognitoUsers`, {
        method: "GET",
        headers: getAuthHeaders(),
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data?.error || data?.message || `HTTP ${resp.status}`);
      }
      setUsers(data.users || []);
      setTotalCount(data.totalCount || 0);
    } catch (err) {
      setError(err.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const post = useCallback(
    async (payload) => {
      const resp = await fetch(API_URL, {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data?.error || data?.message || `HTTP ${resp.status}`);
      }
      return data;
    },
    [getAuthHeaders]
  );

  const runAction = async (key, label, payload) => {
    setBusyKey(key);
    setActionMessage("");
    try {
      await post(payload);
      setActionMessage(label);
      await loadUsers();
    } catch (err) {
      setActionMessage(`Failed: ${err.message || err}`);
    } finally {
      setBusyKey("");
    }
  };

  const handleApproved = (u, value) => {
    const who = u.email || u.username;
    if (!value && !window.confirm(`Remove approval from ${who}?`)) return;
    runAction(
      `approved:${u.username}`,
      `${value ? "Approved" : "Un-approved"} ${who}.`,
      {
        type: "setUserAccess",
        sub: u.sub,
        email: u.email,
        name: u.name,
        field: "approved",
        value,
      }
    );
  };

  const handlePaid = (u, value) => {
    const who = u.email || u.username;
    const msg = value
      ? `Mark ${who} as a subscriber? They'll see event details like a paying member (this does NOT create a Stripe subscription).`
      : `Remove subscriber access from ${who}?`;
    if (!window.confirm(msg)) return;
    runAction(
      `paid:${u.username}`,
      `${value ? "Marked" : "Removed"} ${who} ${value ? "as subscriber" : "from subscribers"}.`,
      {
        type: "setUserAccess",
        sub: u.sub,
        email: u.email,
        name: u.name,
        field: "paid",
        value,
      }
    );
  };

  const handleCaptain = (u, promote) => {
    const who = u.email || u.username;
    if (!promote && !window.confirm(`Remove ${who} from Captains?`)) return;
    runAction(
      `captain:${u.username}`,
      `${promote ? "Promoted" : "Removed"} ${who} ${promote ? "to" : "from"} Captains.`,
      promote
        ? { type: "promoteCaptain", email: u.email }
        : { type: "demoteCaptain", username: u.username }
    );
  };

  const stats = useMemo(() => {
    let new7 = 0;
    let subscribers = 0;
    let captains = 0;
    users.forEach((u) => {
      if (isWithinDays(u.createdAt, 7)) new7 += 1;
      if (u.paid === true) subscribers += 1;
      if (u.isCaptain) captains += 1;
    });
    return { new7, subscribers, captains };
  }, [users]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (q) {
        const haystack = [u.email, u.name, u.username]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      switch (filter) {
        case "new7":
          return isWithinDays(u.createdAt, 7);
        case "unapproved":
          return u.approved !== true && !u.isAdmin;
        case "subscribers":
          return u.paid === true;
        case "captains":
          return u.isCaptain;
        default:
          return true;
      }
    });
  }, [users, search, filter]);

  return (
    <div className="users-tab">
      <div className="users-tab__header">
        <h2 className="users-tab__title">Users</h2>
        <button
          type="button"
          className="users-tab__btn"
          onClick={loadUsers}
          disabled={loading}
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>
      <p className="users-tab__hint">
        Every account in the Cognito pool, newest first. Approve new joiners,
        promote captains, or grant subscriber access — all from here. Marking
        someone a subscriber gives them paid-member visibility without a
        Stripe subscription (use it for legacy/OG members or comps).
      </p>

      <div className="users-tab__tiles">
        <div className="users-tab__tile">
          <div className="users-tab__tile-num">{totalCount}</div>
          <div className="users-tab__tile-label">accounts in pool</div>
        </div>
        <div className="users-tab__tile users-tab__tile--new">
          <div className="users-tab__tile-num">{stats.new7}</div>
          <div className="users-tab__tile-label">joined this week</div>
        </div>
        <div className="users-tab__tile">
          <div className="users-tab__tile-num">{stats.subscribers}</div>
          <div className="users-tab__tile-label">subscribers</div>
        </div>
        <div className="users-tab__tile">
          <div className="users-tab__tile-num">{stats.captains}</div>
          <div className="users-tab__tile-label">captains</div>
        </div>
      </div>

      <div className="users-tab__toolbar">
        <input
          type="search"
          className="users-tab__search"
          placeholder="Search by email or name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="users-tab__select"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        >
          {FILTERS.map((f) => (
            <option key={f.id} value={f.id}>
              {f.label}
            </option>
          ))}
        </select>
        <span className="users-tab__count">
          {loading ? "Loading…" : `${filteredUsers.length} shown`}
        </span>
      </div>

      {error && <div className="users-tab__error">Error: {error}</div>}
      {actionMessage && (
        <div className="users-tab__info">{actionMessage}</div>
      )}

      <div className="users-tab__table-wrap">
        <table className="users-tab__table">
          <thead>
            <tr>
              <th>Joined</th>
              <th>User</th>
              <th>Access</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && filteredUsers.length === 0 && (
              <tr>
                <td colSpan={4} className="users-tab__empty">
                  {search || filter !== "all"
                    ? "No users match the current filters."
                    : "No users in the pool."}
                </td>
              </tr>
            )}
            {filteredUsers.map((u) => {
              const isNew = isWithinDays(u.createdAt, 7);
              const approvedBusy = busyKey === `approved:${u.username}`;
              const paidBusy = busyKey === `paid:${u.username}`;
              const captainBusy = busyKey === `captain:${u.username}`;
              return (
                <tr key={u.username}>
                  <td className="users-tab__date">
                    {formatJoined(u.createdAt)}
                    {isNew && (
                      <div className="users-tab__badge users-tab__badge--new">
                        new
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="users-tab__who">
                      <div className="users-tab__email">
                        {u.email || u.username}
                      </div>
                      {u.name && (
                        <div className="users-tab__muted-sub">{u.name}</div>
                      )}
                      {(u.status !== "CONFIRMED" || !u.enabled) && (
                        <div className="users-tab__muted-sub">
                          {!u.enabled ? "DISABLED" : u.status}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="users-tab__badges">
                      {u.isAdmin && (
                        <span className="users-tab__badge users-tab__badge--admin">
                          admin
                        </span>
                      )}
                      {u.isCaptain && (
                        <span className="users-tab__badge users-tab__badge--captain">
                          captain
                        </span>
                      )}
                      {u.paid === true && (
                        <span className="users-tab__badge users-tab__badge--sub">
                          subscriber
                        </span>
                      )}
                      {u.approved === true && (
                        <span className="users-tab__badge users-tab__badge--approved">
                          approved
                        </span>
                      )}
                      {!u.isAdmin &&
                        !u.isCaptain &&
                        u.paid !== true &&
                        u.approved !== true && (
                          <span className="users-tab__muted">
                            {u.approved === null && u.paid === null
                              ? "no profile row yet"
                              : "no access granted"}
                          </span>
                        )}
                    </div>
                  </td>
                  <td className="users-tab__actions">
                    {u.isAdmin ? (
                      <span className="users-tab__muted">
                        manage admins in AWS console
                      </span>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="users-tab__btn"
                          onClick={() => handleApproved(u, u.approved !== true)}
                          disabled={approvedBusy}
                          title="Approved users are matched into GroupMe + mailing list flows"
                        >
                          {approvedBusy
                            ? "…"
                            : u.approved === true
                              ? "Un-approve"
                              : "Approve"}
                        </button>
                        <button
                          type="button"
                          className="users-tab__btn"
                          onClick={() => handlePaid(u, u.paid !== true)}
                          disabled={paidBusy}
                          title="Subscribers see full event details (grants access only — no Stripe billing)"
                        >
                          {paidBusy
                            ? "…"
                            : u.paid === true
                              ? "Remove subscriber"
                              : "Mark subscriber"}
                        </button>
                        <button
                          type="button"
                          className={
                            u.isCaptain
                              ? "users-tab__btn users-tab__btn--danger"
                              : "users-tab__btn"
                          }
                          onClick={() => handleCaptain(u, !u.isCaptain)}
                          disabled={captainBusy || (!u.isCaptain && !u.email)}
                          title="Captains can manage events, photos, and locations"
                        >
                          {captainBusy
                            ? "…"
                            : u.isCaptain
                              ? "Remove captain"
                              : "Make captain"}
                        </button>
                        <button
                          type="button"
                          className="users-tab__btn"
                          onClick={() => setLinking(u)}
                          title="Pair this Cognito account with an existing socialUsers profile row (e.g. an OG subscriber's record under a different email)"
                        >
                          Link profile…
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {linking && (
        <LinkProfileModal
          cognitoUser={linking}
          adminEmail={userInfo?.email || null}
          getAuthHeaders={getAuthHeaders}
          onClose={() => setLinking(null)}
          onLinked={(msg) => {
            setLinking(null);
            setActionMessage(msg);
            loadUsers();
          }}
        />
      )}
    </div>
  );
};

// Pairing modal: search socialUsers (email / name / subscriber status,
// same picker style as the Rewards tab), expand any candidate to see
// the ENTIRE DynamoDB record, then Pair — which stamps cognitoSub onto
// the chosen row so the roster join, profile photo lookup, and event
// visibility all resolve to that record for this Cognito account.
const LinkProfileModal = ({
  cognitoUser,
  adminEmail,
  getAuthHeaders,
  onClose,
  onLinked,
}) => {
  const [query, setQuery] = useState(cognitoUser.email || "");
  const [expanded, setExpanded] = useState(null); // userId of open record
  const [records, setRecords] = useState({}); // userId -> full row | {error}
  const [pairing, setPairing] = useState("");
  const debounceRef = useRef(null);

  const [runSearch, { data, loading, error }] = useLazyQuery(
    SEARCH_SOCIAL_USERS,
    { fetchPolicy: "network-only" }
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) return;
    debounceRef.current = setTimeout(() => {
      runSearch({ variables: { query: q } });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, runSearch]);

  const results = data?.searchSocialUsers || [];

  const fetchFullRecord = async (userId) => {
    try {
      const resp = await fetch(
        `${API_URL}?type=getSocialUser&userId=${encodeURIComponent(userId)}`,
        { method: "GET", headers: getAuthHeaders() }
      );
      const out = await resp.json();
      if (!resp.ok) {
        throw new Error(out?.error || out?.message || `HTTP ${resp.status}`);
      }
      return out.user;
    } catch (err) {
      return { __error: err.message || String(err) };
    }
  };

  const toggleExpand = async (row) => {
    if (expanded === row.userId) {
      setExpanded(null);
      return;
    }
    setExpanded(row.userId);
    if (!records[row.userId]) {
      const rec = await fetchFullRecord(row.userId);
      setRecords((prev) => ({ ...prev, [row.userId]: rec }));
    }
  };

  const handlePair = async (row) => {
    setPairing(row.userId);
    try {
      // Pull the full record first so we can warn before stealing a row
      // that's already linked to a DIFFERENT Cognito account.
      const rec = records[row.userId] || (await fetchFullRecord(row.userId));
      if (rec && rec.__error) throw new Error(rec.__error);
      const existingSub = rec && rec.cognitoSub;
      const who = row.email || row.userId;
      let msg = `Pair ${cognitoUser.email || cognitoUser.username} with the profile row for ${who}?`;
      if (existingSub && existingSub !== cognitoUser.sub) {
        msg =
          `WARNING: this row is already linked to a different Cognito account ` +
          `(sub ${existingSub}). Re-pairing will point it at ` +
          `${cognitoUser.email || cognitoUser.username} instead. Continue?`;
      }
      if (!window.confirm(msg)) {
        setPairing("");
        return;
      }
      const resp = await fetch(API_URL, {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "editUser",
          userId: row.userId,
          fields: {
            cognitoSub: cognitoUser.sub,
            cognitoLinkedAt: new Date().toISOString(),
            cognitoLinkedBy: adminEmail || "admin-ui",
          },
        }),
      });
      const out = await resp.json();
      if (!resp.ok) {
        throw new Error(out?.error || out?.message || `HTTP ${resp.status}`);
      }
      onLinked(
        `Paired ${cognitoUser.email || cognitoUser.username} with ${who}'s profile row.`
      );
    } catch (err) {
      window.alert(`Pairing failed: ${err.message || err}`);
    } finally {
      setPairing("");
    }
  };

  return (
    <div
      className="users-modal__backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="users-modal__panel"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="users-modal__header">
          <h3 className="users-modal__title">Link profile</h3>
          <button
            type="button"
            className="users-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <p className="users-modal__hint">
          Pairing <strong>{cognitoUser.email || cognitoUser.username}</strong>{" "}
          with an existing profile row. Search below (their old email, a
          nickname, their Stripe email…), open "Full record" to inspect a
          candidate, then Pair the right one.
        </p>

        <input
          type="search"
          className="users-modal__search"
          placeholder="Search by name, email, or Stripe email…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />
        {query.trim().length > 0 && query.trim().length < 2 && (
          <div className="users-modal__hint">Type at least 2 characters…</div>
        )}
        {loading && <div className="users-modal__hint">Searching…</div>}
        {error && (
          <div className="users-modal__error">
            Search failed: {error.message}
          </div>
        )}
        {!loading &&
          !error &&
          query.trim().length >= 2 &&
          results.length === 0 && (
            <div className="users-modal__hint">
              No matches for "{query.trim()}".
            </div>
          )}

        <div className="users-modal__results">
          {results.map((row) => {
            const name =
              [row.firstName, row.lastName].filter(Boolean).join(" ") ||
              row.name ||
              row.email ||
              row.userId;
            const rec = records[row.userId];
            const isOpen = expanded === row.userId;
            return (
              <div key={row.userId} className="users-modal__row">
                <div className="users-modal__row-top">
                  <div className="users-modal__row-main">
                    <div className="users-modal__row-name">{name}</div>
                    <div className="users-modal__row-meta">
                      <span>{row.email || "(no email)"}</span>
                      {row.stripeEmail && row.stripeEmail !== row.email && (
                        <span>· stripe: {row.stripeEmail}</span>
                      )}
                      <span
                        className={
                          row.paid
                            ? "users-tab__badge users-tab__badge--sub"
                            : "users-tab__badge users-tab__badge--none"
                        }
                      >
                        {row.paid ? "subscriber" : "non-subscriber"}
                      </span>
                    </div>
                  </div>
                  <div className="users-modal__row-actions">
                    <button
                      type="button"
                      className="users-tab__btn"
                      onClick={() => toggleExpand(row)}
                    >
                      {isOpen ? "Hide record" : "Full record"}
                    </button>
                    <button
                      type="button"
                      className="users-tab__btn users-tab__btn--primary"
                      onClick={() => handlePair(row)}
                      disabled={pairing === row.userId}
                    >
                      {pairing === row.userId ? "Pairing…" : "Pair"}
                    </button>
                  </div>
                </div>
                {isOpen && (
                  <div className="users-modal__record">
                    {!rec && (
                      <div className="users-modal__hint">Loading record…</div>
                    )}
                    {rec && rec.__error && (
                      <div className="users-modal__error">
                        Couldn't load record: {rec.__error}
                      </div>
                    )}
                    {rec && !rec.__error && (
                      <table className="users-modal__record-table">
                        <tbody>
                          {Object.keys(rec)
                            .sort()
                            .map((k) => (
                              <tr key={k}>
                                <td className="users-modal__record-key">
                                  {k}
                                </td>
                                <td className="users-modal__record-val">
                                  {typeof rec[k] === "object"
                                    ? JSON.stringify(rec[k])
                                    : String(rec[k])}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default UsersTab;
